<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><i class="fa fa-heartbeat"></i> {{ $t('diagTitle') }}</h1>
        <p class="mb-0 text-muted">{{ $t('diagSubtitle') }}</p>
      </div>
      <ul class="app-breadcrumb breadcrumb">
        <li class="breadcrumb-item"><i class="fa fa-home fa-lg"></i></li>
        <li class="breadcrumb-item">{{ $t('diagTitle') }}</li>
      </ul>
    </div>

    <div class="d-flex align-items-center mb-3">
      <span class="text-muted mr-2">{{ $t('diagPeriod') }}:</span>
      <select v-model="rangeKey" class="form-control form-control-sm" style="width:auto" @change="loadAll">
        <option value="today">{{ $t('diagToday') }}</option>
        <option value="7d">{{ $t('diag7d') }}</option>
        <option value="30d">{{ $t('diag30d') }}</option>
      </select>
      <button class="btn btn-sm btn-outline-secondary ml-2" :disabled="loading" @click="loadAll">
        <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i> {{ $t('diagRefresh') }}
      </button>
      <span v-if="lastUpdatedStr" class="text-muted small ml-3"><i class="fa fa-clock-o"></i> {{ $t('diagUpdatedAt') }} {{ lastUpdatedStr }} <span class="text-success">({{ $t('diagAuto') }})</span></span>
    </div>

    <!-- Thẻ tổng quan -->
    <div class="row diag-cards mb-3">
      <div class="col-md">
        <div class="card stat-card h-100"><div class="card-body text-center">
          <i class="fa fa-user-circle text-secondary"></i>
          <div class="stat-value">{{ overview.agents || 0 }}</div>
          <div class="stat-label">{{ $t('diagAgents') }}</div>
        </div></div>
      </div>
      <div class="col-md">
        <div class="card stat-card h-100"><div class="card-body text-center">
          <i class="fa fa-server" :class="statusCls(levelLatency(overview.apiP95, 800, 2000))"></i>
          <div class="stat-value" :class="statusCls(levelLatency(overview.apiP95, 800, 2000))">{{ overview.apiP95 || 0 }}<small>ms</small></div>
          <div class="stat-label" :title="$t('diagApiHint')">{{ $t('diagApi') }} <i class="fa fa-question-circle text-muted"></i></div>
        </div></div>
      </div>
      <div class="col-md">
        <div class="card stat-card h-100"><div class="card-body text-center">
          <i class="fa fa-phone" :class="statusCls(levelMos(overview.mos))"></i>
          <div class="stat-value" :class="statusCls(levelMos(overview.mos))">{{ overview.mos || '—' }}</div>
          <div class="stat-label" :title="$t('diagMosHint')">{{ $t('diagMos') }} <i class="fa fa-question-circle text-muted"></i></div>
        </div></div>
      </div>
      <div class="col-md">
        <div class="card stat-card h-100"><div class="card-body text-center">
          <i class="fa fa-plug" :class="overview.reconnects ? 'text-warning' : 'text-success'"></i>
          <div class="stat-value">{{ overview.reconnects || 0 }}</div>
          <div class="stat-label" :title="$t('diagReconnectsHint')">{{ $t('diagReconnects') }} <i class="fa fa-question-circle text-muted"></i></div>
        </div></div>
      </div>
      <div class="col-md">
        <div class="card stat-card h-100"><div class="card-body text-center">
          <i class="fa fa-shield" :class="overview.firewallBlocked ? 'text-danger' : 'text-success'"></i>
          <div class="stat-value" :class="overview.firewallBlocked ? 'text-danger' : ''">{{ overview.firewallBlocked || 0 }}</div>
          <div class="stat-label" :title="$t('diagFirewallHint')">{{ $t('diagFirewall') }} <i class="fa fa-question-circle text-muted"></i></div>
        </div></div>
      </div>
    </div>

    <!-- Khách hàng -->
    <div class="card mb-3">
      <div class="card-header"><i class="fa fa-building text-primary"></i> {{ $t('diagClientsTitle') }}</div>
      <div class="table-responsive">
        <table class="table table-hover mb-0 align-middle">
          <thead class="thead-light"><tr>
            <th>{{ $t('diagClient') }}</th>
            <th class="text-center">{{ $t('diagAgents') }}</th>
            <th class="text-center" :title="$t('diagApiHint')">{{ $t('diagApi') }}</th>
            <th class="text-center" :title="$t('diagMosHint')">{{ $t('diagMos') }}</th>
            <th class="text-center">{{ $t('diagReconnects') }}</th>
            <th class="text-center">{{ $t('diagFirewall') }}</th>
            <th></th>
          </tr></thead>
          <tbody>
            <tr v-for="c in clients" :key="c._id">
              <td><b>{{ c.clientName }}</b><small v-if="c.domain" class="text-muted d-block">{{ c.domain }}</small></td>
              <td class="text-center">{{ c.agents }}</td>
              <td class="text-center"><span class="badge" :class="badgeCls(levelLatency(c.apiP95, 800, 2000))">{{ c.apiP95 }}ms · {{ statusWord(levelLatency(c.apiP95, 800, 2000)) }}</span></td>
              <td class="text-center"><span class="badge" :class="badgeCls(levelMos(c.mos))">{{ c.mos || '—' }}<template v-if="c.mos"> · {{ statusWord(levelMos(c.mos)) }}</template></span></td>
              <td class="text-center"><span :class="c.reconnects ? 'text-warning font-weight-bold' : 'text-muted'">{{ c.reconnects }}</span></td>
              <td class="text-center"><span :class="c.firewallBlocked ? 'text-danger font-weight-bold' : 'text-muted'">{{ c.firewallBlocked }}</span></td>
              <td class="text-right">
                <button class="btn btn-sm btn-outline-primary" @click="selectClient(c)"><i class="fa fa-users"></i> {{ $t('diagViewAgents') }}</button>
                <button v-if="canDelete" class="btn btn-sm btn-outline-danger is-border-1 ml-1" :title="$t('diagDelete')" @click="deleteClient(c)"><i class="fa fa-trash"></i></button>
              </td>
            </tr>
            <tr v-if="!clients.length"><td colspan="7" class="text-center text-muted py-4"><i class="fa fa-inbox fa-2x d-block mb-2"></i>{{ $t('diagEmpty') }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Nhân viên của khách hàng -->
    <div class="card mb-3" v-if="selectedClient">
      <div class="card-header"><i class="fa fa-users text-primary"></i> {{ $t('diagAgentsOf') }} <b>{{ selectedClient.clientName }}</b><small v-if="selectedClient.domain" class="text-muted"> · {{ selectedClient.domain }}</small></div>
      <div class="table-responsive">
        <table class="table table-hover mb-0 align-middle">
          <thead class="thead-light"><tr>
            <th>{{ $t('diagAgent') }}</th>
            <th class="text-center" :title="$t('diagApiHint')">{{ $t('diagApi') }}</th>
            <th class="text-center">{{ $t('diagMos') }}</th>
            <th class="text-center" :title="$t('diagRealtimeHint')">{{ $t('diagRealtime') }}</th>
            <th class="text-center">{{ $t('diagReconnects') }}</th>
            <th class="text-center" :title="$t('diagSmoothHint')">{{ $t('diagSmooth') }}</th>
            <th class="text-center">{{ $t('diagMemory') }}</th>
            <th></th>
          </tr></thead>
          <tbody>
            <tr v-for="u in users" :key="u._id">
              <td><b>{{ u.name }}</b> <small class="text-muted" v-if="u.ext">· {{ u.ext }}</small></td>
              <td class="text-center"><span class="badge" :class="badgeCls(levelLatency(u.apiP95, 800, 2000))">{{ u.apiP95 }}ms</span></td>
              <td class="text-center"><span class="badge" :class="badgeCls(levelMos(u.mos))">{{ u.mos || '—' }}</span></td>
              <td class="text-center">{{ u.socketRtt ? u.socketRtt + 'ms' : '—' }}</td>
              <td class="text-center"><span :class="u.reconnects ? 'text-warning font-weight-bold' : 'text-muted'">{{ u.reconnects }}</span></td>
              <td class="text-center"><span :class="u.fps && u.fps < 30 ? 'text-danger font-weight-bold' : ''">{{ u.fps || '—' }}<small v-if="u.fps"> fps</small></span></td>
              <td class="text-center">{{ u.heap ? u.heap + ' MB' : '—' }}</td>
              <td class="text-right">
                <button class="btn btn-sm btn-outline-secondary" @click="selectUser(u)"><i class="fa fa-history"></i> {{ $t('diagHistory') }}</button>
                <button v-if="canDiagnose" class="btn btn-sm btn-outline-info is-border-1 ml-1" @click="openDiagnosis(u)"><i class="fa fa-magic"></i> {{ $t('diagDiagnose') }}</button>
                <button v-if="canDelete" class="btn btn-sm btn-outline-danger is-border-1 ml-1" :title="$t('diagDelete')" @click="deleteUser(u)"><i class="fa fa-trash"></i></button>
              </td>
            </tr>
            <tr v-if="!users.length"><td colspan="8" class="text-center text-muted py-4">{{ $t('diagEmpty') }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Lịch sử của nhân viên -->
    <div class="card" v-if="selectedUser">
      <div class="card-header d-flex align-items-center">
        <span><i class="fa fa-history text-primary"></i> {{ $t('diagHistoryOf') }} <b>{{ selectedUser.name }}</b></span>
        <button v-if="canDelete" class="btn btn-sm btn-outline-danger is-border-1 ml-auto" @click="deleteUser(selectedUser)"><i class="fa fa-trash"></i> {{ $t('diagDelete') }}</button>
      </div>
      <div class="table-responsive" style="max-height:420px;overflow:auto">
        <table class="table table-sm mb-0">
          <thead class="thead-light"><tr>
            <th>{{ $t('diagTime') }}</th>
            <th :title="$t('diagApiHint')">{{ $t('diagApi') }}</th>
            <th :title="$t('diagTtfbHint')">{{ $t('diagTtfb') }}</th>
            <th>{{ $t('diagSlowEp') }}</th>
            <th :title="$t('diagRealtimeHint')">{{ $t('diagRealtime') }}</th>
            <th>{{ $t('diagMos') }}</th>
            <th>{{ $t('diagSmooth') }}</th>
            <th>{{ $t('diagMemory') }}</th>
            <th>{{ $t('diagNetwork') }}</th>
            <th>{{ $t('diagSource') }}</th>
          </tr></thead>
          <tbody>
            <tr v-for="(d, i) in timeline" :key="i">
              <td class="text-nowrap">{{ fmtTime(d.bucketTime) }}</td>
              <td :class="statusCls(levelLatency(d.api && d.api.p95, 800, 2000))">{{ d.api && d.api.p95 != null ? d.api.p95 + 'ms' : '—' }}</td>
              <td>{{ d.api && d.api.ttfb != null ? d.api.ttfb + 'ms' : '—' }}</td>
              <td><small class="text-muted">{{ (d.api && d.api.slowestEndpoint) || '—' }}</small></td>
              <td>{{ d.socket && d.socket.rtt != null ? d.socket.rtt + 'ms' : '—' }}</td>
              <td :class="statusCls(levelMos(d.rtp && d.rtp.mos))">{{ d.rtp && d.rtp.mos != null ? d.rtp.mos : '—' }}</td>
              <td :class="d.fe && d.fe.fps != null && d.fe.fps < 30 ? 'text-danger' : ''">{{ d.fe && d.fe.fps != null ? d.fe.fps + ' fps' : '—' }}</td>
              <td>{{ d.fe && d.fe.heapUsedMb != null ? d.fe.heapUsedMb + ' MB' : '—' }}</td>
              <td><small>{{ netLabel(d) }}{{ d.region ? ' · ' + d.region : '' }}</small></td>
              <td><span class="badge badge-light">{{ srcLabel(d.source) }}</span></td>
            </tr>
            <tr v-if="!timeline.length"><td colspan="10" class="text-center text-muted py-4">{{ $t('diagEmpty') }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'AdminDiagnostic',
  data() {
    return {
      rangeKey: '7d', loading: false, lastUpdatedStr: '',
      overview: {}, clients: [],
      selectedClient: null, users: [],
      selectedUser: null, timeline: [],
    };
  },
  computed: {
    // Xoá là thao tác phá huỷ → chỉ owner/admin/tech (khớp role.check ở backend) mới thấy nút.
    canDelete() {
      const role = this.$store.state.user && this.$store.state.user.role;
      return ['owner', 'admin', 'tech'].includes(role);
    },
    // Chẩn đoán AI tốn chi phí LLM → cùng nhóm quyền với xoá.
    canDiagnose() { return this.canDelete; },
  },
  mounted() {
    this.loadAll();
    // Auto-refresh mỗi 10s — chỉ làm mới tổng quan + danh sách, KHÔNG reset drill-down.
    this._refreshTimer = setInterval(() => this.refreshTop(), 10000);
  },
  beforeDestroy() {
    if (this._refreshTimer) { clearInterval(this._refreshTimer); this._refreshTimer = null; }
  },
  methods: {
    async refreshTop() {
      const { from, to } = this.computeRange();
      try {
        const [ov, cl] = await Promise.all([
          this.$service.get('/diagnostic/overview', { params: { from, to } }),
          this.$service.get('/diagnostic/clients', { params: { from, to } }),
        ]);
        this.overview = ov.data || {};
        this.clients = (cl.data && cl.data.docs) || [];
        this.lastUpdatedStr = new Date().toLocaleTimeString('vi-VN');
      } catch (e) { /* giữ dữ liệu cũ nếu lỗi */ }
    },
    computeRange() {
      const end = Date.now();
      let start;
      if (this.rangeKey === 'today') { const d = new Date(); d.setHours(0, 0, 0, 0); start = d.getTime(); }
      else if (this.rangeKey === '30d') start = end - 30 * 24 * 3600 * 1000;
      else start = end - 7 * 24 * 3600 * 1000;
      return { from: start, to: end };
    },
    async loadAll() {
      this.loading = true;
      this.selectedClient = null; this.users = []; this.selectedUser = null; this.timeline = [];
      const { from, to } = this.computeRange();
      try {
        const [ov, cl] = await Promise.all([
          this.$service.get('/diagnostic/overview', { params: { from, to } }),
          this.$service.get('/diagnostic/clients', { params: { from, to } }),
        ]);
        this.overview = ov.data || {};
        this.clients = (cl.data && cl.data.docs) || [];
        this.lastUpdatedStr = new Date().toLocaleTimeString('vi-VN');
      } catch (e) { this.clients = []; this.overview = {}; }
      finally { this.loading = false; }
    },
    async selectClient(c) {
      this.selectedClient = c; this.users = []; this.selectedUser = null; this.timeline = [];
      const { from, to } = this.computeRange();
      try {
        const r = await this.$service.get(`/diagnostic/client/${c._id}/users`, { params: { from, to } });
        this.users = (r.data && r.data.docs) || [];
      } catch (e) { this.users = []; }
    },
    async selectUser(u) {
      this.selectedUser = u; this.timeline = [];
      const { from, to } = this.computeRange();
      try {
        const r = await this.$service.get(`/diagnostic/user/${u._id}`, { params: { from, to } });
        this.timeline = (r.data && r.data.docs) || [];
      } catch (e) { this.timeline = []; }
    },
    // Xoá datapoint của 1 KH trong khoảng đang chọn → reload toàn trang.
    async deleteClient(c) {
      const r = await this.$swal({ icon: 'warning', text: this.$t('diagDelClientConfirm', { name: c.clientName }), showCancelButton: true, confirmButtonText: this.$t('diagDelete') });
      if (!r.value) return;
      const loader = this.$loading.show();
      const { from, to } = this.computeRange();
      try {
        const res = await this.$service.delete(`/diagnostic/client/${c._id}`, { params: { from, to } });
        const n = (res.data && res.data.deleted) || 0;
        await this.loadAll();
        this.$swal({ icon: 'success', text: this.$t('diagDeleted', { n }), timer: 2000, showConfirmButton: false });
      } catch (e) { this.$swal({ icon: 'error', text: this.$t('diagDelFail') }); }
      finally { loader.hide(); }
    },
    // Xoá datapoint của 1 nhân viên trong khoảng đang chọn → reload lại lịch sử + danh sách trên.
    async deleteUser(u) {
      const r = await this.$swal({ icon: 'warning', text: this.$t('diagDelUserConfirm', { name: u.name }), showCancelButton: true, confirmButtonText: this.$t('diagDelete') });
      if (!r.value) return;
      const loader = this.$loading.show();
      const { from, to } = this.computeRange();
      try {
        const res = await this.$service.delete(`/diagnostic/user/${u._id}`, { params: { from, to } });
        const n = (res.data && res.data.deleted) || 0;
        this.timeline = [];
        if (this.selectedClient) await this.selectClient(this.selectedClient);
        await this.refreshTop();
        this.$swal({ icon: 'success', text: this.$t('diagDeleted', { n }), timer: 2000, showConfirmButton: false });
      } catch (e) { this.$swal({ icon: 'error', text: this.$t('diagDelFail') }); }
      finally { loader.hide(); }
    },
    // Mở chẩn đoán AI: ưu tiên xem bản đã lưu (không tốn LLM); chưa có → phân tích mới.
    async openDiagnosis(u) {
      const loader = this.$loading.show();
      let doc = null;
      try {
        const res = await this.$service.get(`/diagnostic/user/${u._id}/analysis`);
        doc = res.data && res.data.doc;
      } catch (e) { /* không có bản cũ → phân tích mới */ }
      finally { loader.hide(); }
      if (doc && doc.result) this.showDiagResult(doc, u);
      else this.runAnalyze(u);
    },
    // Gọi LLM phân tích mới (tốn chi phí) → lưu + hiển thị.
    async runAnalyze(u) {
      const loader = this.$loading.show();
      const { from, to } = this.computeRange();
      try {
        const res = await this.$service.post(`/diagnostic/user/${u._id}/analyze`, null, { params: { from, to, rangeKey: this.rangeKey } });
        const d = res.data || {};
        if (d.empty) { this.$swal({ icon: 'info', text: this.$t('diagNoData') }); return; }
        if (d.doc && d.doc.result) this.showDiagResult(d.doc, u);
        else this.$swal({ icon: 'error', text: this.$t('diagAiFail') });
      } catch (e) {
        const err = e.response && e.response.data && e.response.data.error;
        this.$swal({ icon: 'error', text: err === 'OPENAI_KEY_MISSING' ? this.$t('diagAiKeyMissing') : this.$t('diagAiFail') });
      } finally { loader.hide(); }
    },
    showDiagResult(doc, u) {
      const who = doc.createdBy && doc.createdBy.name ? ' · ' + doc.createdBy.name : '';
      const meta = `${this.$t('diagAnalyzedAt')} ${this.fmtTime(doc.createTime)}${who}`;
      this.$swal({
        title: `${this.$t('diagDiagnose')} — ${u.name}`,
        html: `<div style="white-space:pre-wrap;text-align:left;max-height:50vh;overflow:auto;font-size:.9rem;line-height:1.5">${this.escapeHtml(doc.result)}</div>`
          + `<div style="text-align:left;font-size:.78rem;color:#6c757d;margin-top:.6rem">${this.escapeHtml(meta)}</div>`,
        showCancelButton: true,
        confirmButtonText: this.$t('diagReanalyze'),
        cancelButtonText: this.$t('diagClose'),
        width: 640,
      }).then(r => { if (r.value) this.runAnalyze(u); });
    },
    escapeHtml(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); },
    fmtTime(ms) { try { return new Date(ms).toLocaleString('vi-VN'); } catch (e) { return ms; } },
    srcLabel(s) { return s === 'manual' ? this.$t('diagSrcManual') : this.$t('diagSrcAuto'); },
    // netMedium = loại mạng vật lý THẬT (wifi/cellular...). netType chỉ là TỐC ĐỘ ước tính (4g kể cả WiFi) → prefix "≈" + ghi rõ là tốc độ.
    netLabel(d) {
      const MEDIUM = { wifi: 'WiFi', cellular: this.$t('diagNetCellular'), ethernet: 'Ethernet', wimax: 'WiMAX', bluetooth: 'Bluetooth' };
      if (d.netMedium) return MEDIUM[d.netMedium] || d.netMedium;
      if (d.netType) return `≈${String(d.netType).toUpperCase()} (${this.$t('diagNetSpeed')})`;
      return '—';
    },
    // mức độ: latency cao = xấu
    levelLatency(v, good, warn) { if (v == null) return null; if (v <= good) return 'good'; if (v <= warn) return 'warn'; return 'bad'; },
    levelMos(v) { if (v == null || !v) return null; if (v >= 4) return 'good'; if (v >= 3) return 'warn'; return 'bad'; },
    statusWord(l) { return { good: this.$t('diagGood'), warn: this.$t('diagWarn'), bad: this.$t('diagBad') }[l] || ''; },
    statusCls(l) { return { good: 'text-success', warn: 'text-warning', bad: 'text-danger' }[l] || 'text-muted'; },
    badgeCls(l) { return { good: 'badge-success', warn: 'badge-warning', bad: 'badge-danger' }[l] || 'badge-light'; },
  },
  i18n: {
    messages: {
      vi: {
        diagTitle: 'Chẩn đoán hiệu năng',
        diagSubtitle: 'Theo dõi sức khoẻ kết nối & cuộc gọi của khách hàng để biết ai đang gặp vấn đề và vì sao.',
        diagPeriod: 'Khoảng thời gian', diagToday: 'Hôm nay', diag7d: '7 ngày qua', diag30d: '30 ngày qua', diagRefresh: 'Tải lại',
        diagUpdatedAt: 'Cập nhật lúc', diagAuto: 'tự động 10s',
        diagAgents: 'Nhân viên',
        diagApi: 'Độ trễ hệ thống', diagApiHint: 'Thời gian máy chủ Callio phản hồi (p95 — 95% yêu cầu nhanh hơn mức này). Càng thấp càng tốt.',
        diagMos: 'Chất lượng gọi', diagMosHint: 'Điểm chất lượng âm thanh cuộc gọi (1–5). ≥4 tốt, <3 kém.',
        diagReconnects: 'Rớt kết nối', diagReconnectsHint: 'Số lần mất kết nối thời gian thực (tin nhắn/cuộc gọi đến trễ).',
        diagFirewall: 'Bị chặn tường lửa', diagFirewallHint: 'Số lượt phát hiện mạng/tường lửa chặn cổng gọi (UDP/TURN) → mất tiếng.',
        diagRealtime: 'Độ trễ realtime', diagRealtimeHint: 'Độ trễ kết nối thời gian thực (WebSocket). Càng thấp càng nhạy.',
        diagSmooth: 'Độ mượt', diagSmoothHint: 'Khung hình/giây của giao diện (FPS). <30 = giật.',
        diagMemory: 'Bộ nhớ',
        diagTtfb: 'Xử lý máy chủ', diagTtfbHint: 'Thời gian máy chủ bắt đầu trả dữ liệu (TTFB) — tách phần chậm do máy chủ vs do mạng.',
        diagSlowEp: 'Chức năng chậm nhất',
        diagNetwork: 'Mạng', diagSource: 'Nguồn', diagSrcManual: 'Nhân viên gửi', diagSrcAuto: 'Tự động',
        diagNetCellular: 'Di động', diagNetSpeed: 'tốc độ',
        diagTime: 'Thời gian',
        diagClientsTitle: 'Khách hàng cần chú ý', diagClient: 'Khách hàng', diagViewAgents: 'Xem nhân viên',
        diagAgentsOf: 'Nhân viên của', diagAgent: 'Nhân viên', diagHistory: 'Lịch sử', diagHistoryOf: 'Lịch sử của',
        diagGood: 'Tốt', diagWarn: 'Cảnh báo', diagBad: 'Kém', diagEmpty: 'Chưa có dữ liệu trong khoảng thời gian này',
        diagDelete: 'Xoá', diagDelFail: 'Xoá thất bại',
        diagDeleted: 'Đã xoá {n} bản ghi',
        diagDelClientConfirm: 'Xoá toàn bộ dữ liệu chẩn đoán của "{name}" trong khoảng thời gian đang chọn?',
        diagDelUserConfirm: 'Xoá toàn bộ dữ liệu chẩn đoán của "{name}" trong khoảng thời gian đang chọn?',
        diagDiagnose: 'Chẩn đoán AI', diagReanalyze: 'Phân tích lại', diagClose: 'Đóng',
        diagAnalyzedAt: 'Phân tích lúc',
        diagNoData: 'Không có dữ liệu để phân tích trong khoảng này',
        diagAiFail: 'Phân tích AI thất bại, thử lại sau',
        diagAiKeyMissing: 'Chưa cấu hình OpenAI key cho AI',
      },
      en: {
        diagTitle: 'Performance diagnostic',
        diagSubtitle: 'Monitor customers’ connection & call health to see who has issues and why.',
        diagPeriod: 'Period', diagToday: 'Today', diag7d: 'Last 7 days', diag30d: 'Last 30 days', diagRefresh: 'Reload',
        diagUpdatedAt: 'Updated at', diagAuto: 'auto 10s',
        diagAgents: 'Agents',
        diagApi: 'System latency', diagApiHint: 'Callio server response time (p95). Lower is better.',
        diagMos: 'Call quality', diagMosHint: 'Audio quality score (1–5). ≥4 good, <3 poor.',
        diagReconnects: 'Disconnects', diagReconnectsHint: 'Real-time disconnect count (delayed messages/calls).',
        diagFirewall: 'Firewall blocked', diagFirewallHint: 'Times network/firewall blocked call media (UDP/TURN).',
        diagRealtime: 'Realtime latency', diagRealtimeHint: 'WebSocket round-trip latency.',
        diagSmooth: 'Smoothness', diagSmoothHint: 'UI frames per second (FPS). <30 = janky.',
        diagMemory: 'Memory',
        diagTtfb: 'Server time', diagTtfbHint: 'Time to first byte (server processing) — separates server vs network slowness.',
        diagSlowEp: 'Slowest function',
        diagNetwork: 'Network', diagSource: 'Source', diagSrcManual: 'Sent by agent', diagSrcAuto: 'Automatic',
        diagNetCellular: 'Cellular', diagNetSpeed: 'speed',
        diagTime: 'Time',
        diagClientsTitle: 'Clients to watch', diagClient: 'Client', diagViewAgents: 'View agents',
        diagAgentsOf: 'Agents of', diagAgent: 'Agent', diagHistory: 'History', diagHistoryOf: 'History of',
        diagGood: 'Good', diagWarn: 'Warning', diagBad: 'Poor', diagEmpty: 'No data in this period',
        diagDelete: 'Delete', diagDelFail: 'Delete failed',
        diagDeleted: 'Deleted {n} records',
        diagDelClientConfirm: 'Delete all diagnostic data of "{name}" in the selected period?',
        diagDelUserConfirm: 'Delete all diagnostic data of "{name}" in the selected period?',
        diagDiagnose: 'AI diagnose', diagReanalyze: 'Re-analyze', diagClose: 'Close',
        diagAnalyzedAt: 'Analyzed at',
        diagNoData: 'No data to analyze in this period',
        diagAiFail: 'AI analysis failed, try again later',
        diagAiKeyMissing: 'OpenAI key not configured',
      },
    },
  },
};
</script>

<style scoped>
.diag-cards .stat-card { border: none; box-shadow: 0 1px 3px rgba(0, 0, 0, .08); border-radius: .5rem; }
.diag-cards .stat-card .fa { font-size: 1.4rem; opacity: .85; }
.diag-cards .stat-value { font-size: 1.6rem; font-weight: 700; line-height: 1.2; margin-top: .25rem; }
.diag-cards .stat-value small { font-size: .9rem; font-weight: 400; }
.diag-cards .stat-label { font-size: .8rem; color: #6c757d; }
.card-header { font-weight: 600; }
.table thead th { font-size: .82rem; text-transform: none; border-top: none; }
</style>
